/**
 * Schema Cache Manager
 * Pre-loads and caches all schemas at app startup for instant access
 */

import apis from '@api/api';
import { Platform } from 'react-native';

// Platform-agnostic storage abstraction
const storage = {
	async getItem(key: string): Promise<string | null> {
		if (Platform.OS === 'web') {
			try {
				return localStorage.getItem(key);
			} catch (error) {
				console.warn(`⚠️ Failed to get item from localStorage:`, error);
				return null;
			}
		} else {
			// React Native - use AsyncStorage
			try {
				const AsyncStorage = (
					await import('@react-native-async-storage/async-storage')
				).default;
				return await AsyncStorage.getItem(key);
			} catch (error) {
				console.warn(`⚠️ Failed to get item from AsyncStorage:`, error);
				return null;
			}
		}
	},
	async setItem(key: string, value: string): Promise<void> {
		if (Platform.OS === 'web') {
			try {
				localStorage.setItem(key, value);
			} catch (error) {
				console.warn(`⚠️ Failed to set item in localStorage:`, error);
			}
		} else {
			// React Native - use AsyncStorage
			try {
				const AsyncStorage = (
					await import('@react-native-async-storage/async-storage')
				).default;
				await AsyncStorage.setItem(key, value);
			} catch (error) {
				console.warn(`⚠️ Failed to set item in AsyncStorage:`, error);
			}
		}
	},
	async removeItem(key: string): Promise<void> {
		if (Platform.OS === 'web') {
			try {
				localStorage.removeItem(key);
			} catch (error) {
				console.warn(
					`⚠️ Failed to remove item from localStorage:`,
					error
				);
			}
		} else {
			// React Native - use AsyncStorage
			try {
				const AsyncStorage = (
					await import('@react-native-async-storage/async-storage')
				).default;
				await AsyncStorage.removeItem(key);
			} catch (error) {
				console.warn(
					`⚠️ Failed to remove item from AsyncStorage:`,
					error
				);
			}
		}
	},
};

interface SchemaCache {
	projectSchema: any;
	profileSchema: any;
	calendarSchema: any;
	taskSchema: any;
	dataSchema: any;
	sampleSchemas: any;
	[key: string]: any;
}

class SchemaCacheManager {
	private static instance: SchemaCacheManager;
	private cache: Partial<SchemaCache> = {};
	private isInitialized = false;
	private initPromise: Promise<void> | null = null;
	private cacheVersion: string;

	private constructor() {
		// Use app version or build number as cache version
		this.cacheVersion = process.env.REACT_APP_VERSION || '1.0.0';
	}

	public static getInstance(): SchemaCacheManager {
		if (!SchemaCacheManager.instance) {
			SchemaCacheManager.instance = new SchemaCacheManager();
		}
		return SchemaCacheManager.instance;
	}

	/**
	 * Initialize and load all schemas
	 */
	public async initialize(): Promise<void> {
		if (this.isInitialized) {
			return;
		}

		if (this.initPromise) {
			return this.initPromise;
		}

		this.initPromise = this._loadAllSchemas();
		await this.initPromise;
	}

	private async _loadAllSchemas(): Promise<void> {
		try {
			if (__DEV__) {
				console.log('📚 Loading all schemas at app startup...');
			}

			// Try to load from storage first (cached)
			const cachedSchemas = await this._loadFromStorage();
			if (cachedSchemas) {
				this.cache = cachedSchemas;
				this.isInitialized = true;
				if (__DEV__) {
					console.log('✅ Loaded schemas from cache');
				}
				// Still fetch in background to update cache
				this._fetchAndCacheSchemas();
				return;
			}

			// No cache - fetch all schemas
			await this._fetchAndCacheSchemas();
		} catch (error) {
			console.error('❌ Failed to load schemas:', error);
			// Continue anyway with empty cache
			this.isInitialized = true;
		}
	}

	private async _fetchAndCacheSchemas(): Promise<void> {
		const schemaPromises = [
			this._fetchSchema('projectSchema', async () => {
				const res = await apis.getProjectSchema();
				return res.schema;
			}),
			this._fetchSchema('profileSchema', async () => {
				const res = await apis.getProfileSchema();
				// Store the full response structure that components expect
				return res.data?.data || res.data || res;
			}),
			this._fetchSchema('wholeCalendarSchema', async () => {
				const res = await apis.getWholeCalendarSchema();
				// Return the whole response or just data depending on structure
				return res.data || res;
			}),
			this._fetchSchema('calendarEntrySchema', async () => {
				const res = await apis.getCalendarSchema();
				// Return the schema directly
				return res?.schema || res?.data?.schema || res;
			}),
			this._fetchSchema('taskSchema', async () => {
				const res = await apis.getTaskSchema();
				return res?.schema || res;
			}),
			// Note: sampleSchemas is page-specific data, not a form schema
			// It's fetched by CreateDataScreen when needed
		];

		await Promise.allSettled(schemaPromises);

		// Save to storage (async)
		await this._saveToStorage();

		this.isInitialized = true;

		if (__DEV__) {
			console.log(
				'✅ All schemas loaded and cached:',
				Object.keys(this.cache).length
			);
		}
	}

	private async _fetchSchema(
		key: string,
		fetcher: () => Promise<any>
	): Promise<void> {
		try {
			const schema = await fetcher();
			if (schema) {
				this.cache[key] = schema;
				if (__DEV__) {
					console.log(
						`✅ Cached ${key}:`,
						Object.keys(schema).length > 0 ? 'Has data' : 'Empty'
					);
				}
			} else {
				console.warn(`⚠️ ${key} returned null/undefined`);
			}
		} catch (error) {
			console.warn(`⚠️ Failed to fetch ${key}:`, error);
			// Continue with other schemas
		}
	}

	private async _loadFromStorage(): Promise<Partial<SchemaCache> | null> {
		try {
			const cached = await storage.getItem('schemaCache');
			const cacheVersion = await storage.getItem('schemaCacheVersion');

			if (cached && cacheVersion === this.cacheVersion) {
				return JSON.parse(cached);
			}
			return null;
		} catch (error) {
			console.warn('⚠️ Failed to load schemas from storage:', error);
			return null;
		}
	}

	private async _saveToStorage(): Promise<void> {
		try {
			await storage.setItem('schemaCache', JSON.stringify(this.cache));
			await storage.setItem('schemaCacheVersion', this.cacheVersion);
			if (__DEV__) {
				console.log('✅ Schemas saved to storage successfully');
			}
		} catch (error) {
			console.warn('⚠️ Failed to save schemas to storage:', error);
		}
	}

	/**
	 * Get a cached schema
	 */
	public getSchema(schemaName: string): any {
		return this.cache[schemaName];
	}

	/**
	 * Get all schemas
	 */
	public getAllSchemas(): Partial<SchemaCache> {
		return { ...this.cache };
	}

	/**
	 * Check if a schema is available
	 */
	public hasSchema(schemaName: string): boolean {
		return !!this.cache[schemaName];
	}

	/**
	 * Clear cache (for testing or when schemas update)
	 */
	public async clearCache(): Promise<void> {
		this.cache = {};
		this.isInitialized = false;
		await storage.removeItem('schemaCache');
		await storage.removeItem('schemaCacheVersion');
	}

	/**
	 * Refresh a specific schema
	 */
	public async refreshSchema(
		schemaName: string,
		fetcher: () => Promise<any>
	): Promise<void> {
		try {
			const schema = await fetcher();
			this.cache[schemaName] = schema;
			await this._saveToStorage();
		} catch (error) {
			console.error(`❌ Failed to refresh ${schemaName}:`, error);
		}
	}

	/**
	 * Get schema by action intent
	 * Maps action types to their corresponding schemas
	 */
	public getSchemaForAction(actionIntent: string): any {
		const schemaMap: Record<string, string> = {
			'ADD PROJECT MODAL DISPLAYED': 'projectSchema',
			'SHOW_PROJECT_MODAL': 'projectSchema',
			'ADDED A NEW PROJECT': 'projectSchema',
			'ADD CALENDAR ENTRY DISPLAYED': 'calendarEntrySchema',
			'SHOW_CALENDAR_ENTRY_MODAL': 'calendarEntrySchema',
			'CALENDAR ENTRY ADDED': 'calendarEntrySchema',
			// Add more mappings as needed
		};

		const schemaKey = schemaMap[actionIntent];
		if (schemaKey && this.cache[schemaKey]) {
			return this.cache[schemaKey];
		}

		// Fallback: Try to infer from intent name
		const intentLower = actionIntent.toLowerCase();
		if (intentLower.includes('project')) return this.cache['projectSchema'];
		if (intentLower.includes('calendar'))
			return this.cache['calendarEntrySchema'];
		if (intentLower.includes('profile')) return this.cache['profileSchema'];
		if (intentLower.includes('task')) return this.cache['taskSchema'];

		return null;
	}
}

// Export singleton instance
export const schemaCache = SchemaCacheManager.getInstance();

// Convenience functions
export const initializeSchemaCache = () => schemaCache.initialize();
export const getSchemaForAction = (actionIntent: string) =>
	schemaCache.getSchemaForAction(actionIntent);
export const getAllSchemas = () => schemaCache.getAllSchemas();
export const hasSchema = (schemaName: string) =>
	schemaCache.hasSchema(schemaName);
